/**
 * Typed wrappers over the callable Cloud Functions. Request/response shapes
 * come from @revbase/shared so client and server can't drift.
 */
import { httpsCallable } from 'firebase/functions';
import type {
  ImportContactsRequest,
  ImportContactsResult,
  AddSuppressionRequest,
  CreateCampaignRequest,
  CreateCampaignResult,
  UpdateCampaignRequest,
  UpdateCampaignResult,
  DeleteCampaignRequest,
  DeleteCampaignResult,
  EnrollContactsRequest,
  EnrollContactsResult,
  EnrichContactsRequest,
  EnrichContactsResult,
  StartMailboxConnectRequest,
  StartMailboxConnectResult,
  DraftSequenceRequest,
  DraftSequenceResult,
  RewriteStepRequest,
  RewriteStepResult,
  GenerateOpenerRequest,
  GenerateOpenerResult,
  RecomputeHealthRequest,
  RecomputeHealthResult,
  RunAuthCheckRequest,
  RunAuthCheckResult,
  ResumeMailboxRequest,
  ResumeMailboxResult,
  SetWarmupRequest,
  SetWarmupResult,
  UpdateWarmupSettingsRequest,
  UpdateWarmupSettingsResult,
  RefreshWarmupThreadsRequest,
  RefreshWarmupThreadsResult,
  SendReplyRequest,
  SendReplyResult,
  SetThreadReadRequest,
  SetThreadReadResult,
  MarkThreadNotInterestedRequest,
  MarkThreadNotInterestedResult,
  GenerateCollateralRequest,
  GenerateCollateralResult,
  PublishLandingPageRequest,
  PublishLandingPageResult,
  RegenerateCollateralRequest,
  RegenerateCollateralResult,
} from '@revbase/shared';
import { functions } from './firebase.js';

function callable<Req, Res>(name: string) {
  const fn = httpsCallable<Req, Res>(functions, name);
  return async (data: Req): Promise<Res> => (await fn(data)).data;
}

export const api = {
  importContacts: callable<ImportContactsRequest, ImportContactsResult>(
    'importContacts',
  ),
  addSuppression: callable<AddSuppressionRequest, { ok: true }>(
    'addSuppression',
  ),
  createCampaign: callable<CreateCampaignRequest, CreateCampaignResult>(
    'createCampaign',
  ),
  updateCampaign: callable<UpdateCampaignRequest, UpdateCampaignResult>(
    'updateCampaign',
  ),
  deleteCampaign: callable<DeleteCampaignRequest, DeleteCampaignResult>(
    'deleteCampaign',
  ),
  enrollContacts: callable<EnrollContactsRequest, EnrollContactsResult>(
    'enrollContacts',
  ),
  enrichContacts: callable<EnrichContactsRequest, EnrichContactsResult>(
    'enrichContacts',
  ),
  startMailboxConnect: callable<
    StartMailboxConnectRequest,
    StartMailboxConnectResult
  >('startMailboxConnect'),
  draftSequence: callable<DraftSequenceRequest, DraftSequenceResult>(
    'draftSequence',
  ),
  rewriteStep: callable<RewriteStepRequest, RewriteStepResult>('rewriteStep'),
  generateOpener: callable<GenerateOpenerRequest, GenerateOpenerResult>(
    'generateOpener',
  ),
  recomputeMailboxHealth: callable<
    RecomputeHealthRequest,
    RecomputeHealthResult
  >('recomputeMailboxHealth'),
  runAuthCheck: callable<RunAuthCheckRequest, RunAuthCheckResult>(
    'runAuthCheck',
  ),
  resumeMailbox: callable<ResumeMailboxRequest, ResumeMailboxResult>(
    'resumeMailbox',
  ),
  setWarmup: callable<SetWarmupRequest, SetWarmupResult>('setWarmup'),
  updateWarmupSettings: callable<
    UpdateWarmupSettingsRequest,
    UpdateWarmupSettingsResult
  >('updateWarmupSettings'),
  refreshWarmupThreads: callable<
    RefreshWarmupThreadsRequest,
    RefreshWarmupThreadsResult
  >('refreshWarmupThreads'),
  sendReply: callable<SendReplyRequest, SendReplyResult>('sendReply'),
  setThreadRead: callable<SetThreadReadRequest, SetThreadReadResult>(
    'setThreadRead',
  ),
  markThreadNotInterested: callable<
    MarkThreadNotInterestedRequest,
    MarkThreadNotInterestedResult
  >('markThreadNotInterested'),
  generateCollateral: callable<
    GenerateCollateralRequest,
    GenerateCollateralResult
  >('generateCollateral'),
  publishLandingPage: callable<
    PublishLandingPageRequest,
    PublishLandingPageResult
  >('publishLandingPage'),
  regenerateCollateral: callable<
    RegenerateCollateralRequest,
    RegenerateCollateralResult
  >('regenerateCollateral'),
};
